import { useState } from "react";
import { css } from "@emotion/react";
import CheckIcon from "@mui/icons-material/Check";
import { Box, Checkbox, Radio, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { firstBy } from "thenby";

import { dayjs } from "@thalamos/common";
import { triggerDownload } from "@thalamos/frontend";
import type { FormContextData } from "@thalamos/aspire-common";
import {
  formIsApplication,
  getBaseFormTemplate,
  getHeadingOrSubtitle,
  getLatestVersion,
  liveMcaForms,
} from "@thalamos/aspire-common";

import { api } from "../api.js";
import { routeFns } from "../routePaths.js";
import { getCompletedApplication } from "./FormProgressPage/helpers/formContextLogic.js";
import { FormRow } from "./FormProgressPage/helpers/FormRow.js";
import type { MenuOptionsType } from "~/components/design-system/index.js";
import {
  calculateWorkItemStatus,
  renderErrorToast,
} from "~/components/design-system/index.js";
import { ConfirmationModal } from "~/pages/ConfirmationModal.js";

export function PatientTimelineCard({
  formContext,
  setPdfViewFormId,
  showRadioSelector,
  showCheckBoxSelector,
  selectedRadioValue,
  setSelectedRadioValue,
  disableCardClick,
  checkedFormItems,
  setCheckedFormItems,
  lastMerged,
}: {
  formContext: Omit<FormContextData, "patient">;
  setPdfViewFormId?: (formId: string | null) => void;
  showRadioSelector?: boolean;
  selectedRadioValue?: string;
  showCheckBoxSelector?: boolean;
  setSelectedRadioValue?: (params: any) => void;
  disableCardClick?: boolean;
  checkedFormItems?: any;
  setCheckedFormItems?: (params: any) => void;
  lastMerged?: string | undefined;
}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const summary = calculateWorkItemStatus(theme, {
    formContextType: formContext.type,
    forms: formContext.forms,
    otherWorkItems: formContext.workItems,
    formContextStatus: formContext.status,
    closedByUserId: formContext.closedByUserId,
    closedByTeamId: formContext.closedByTeamId,
    closedReason: formContext.closedReason,
  });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // For a standalone form context:
  //   - The "main form" is the only form in the context
  // For an MCA assessment:
  //   - The "main form" is the most recently worked on of its forms
  // For an admission form context:
  //   - The "main form" is the most recent Application
  //
  // TODO: Should admission status be more sophisticated?
  const mainForm =
    formContext.type === "standalone"
      ? formContext.forms[0]
      : formContext.type === "mca-assessment"
        ? liveMcaForms(formContext.forms).sort(firstBy("updated", -1))[0]
        : formContext.forms
            .filter(formIsApplication)
            .sort(firstBy("updated", -1))[0];

  const mainFormTemplate =
    mainForm === undefined ? null : getBaseFormTemplate(mainForm);

  const policeIncidentType =
    formContext.type === "police-incident"
      ? formContext.forms.find((f) => f.formTemplate.id === "mha-136")
        ? "s136"
        : "mca"
      : null;

  const admissionHasCompletedApplication = getCompletedApplication(
    formContext.forms,
  );

  const heading: string = getHeadingOrSubtitle({
    formContextType: formContext.type,
    forms: formContext.forms,
    mainFormTemplate,
    applicationTemplate: null,
    isHeading: true,
    admissionHasCompletedApplication: !!admissionHasCompletedApplication,
    policeIncidentType,
  });

  const standaloneForm =
    formContext.type === "standalone" ? formContext.forms[0] : undefined;
  const standaloneSignatures =
    standaloneForm === undefined
      ? []
      : getLatestVersion(standaloneForm).signatures;

  const subheading = `${
    standaloneSignatures.length > 0
      ? `Signed by: ${standaloneSignatures.map((s) => s.userName).join(", ")}.`
      : ""
  }  Last updated:
    ${dayjs.utc(formContext.lastFormUpdated || formContext.updated).fromNow()}`;
  const canDownload =
    mainForm &&
    getLatestVersion(mainForm).signatures.length > 0 &&
    formContext.type !== "admission";

  const canDelete =
    formContext.type === "admission" &&
    formContext.forms
      .flatMap((form) => form.versions)
      .flatMap((formVersion) => formVersion.signatures).length === 0;

  const formContextPage = routeFns.formContextPage(
    formContext.id,
    formContext.patientId,
  );
  const isOnFormPage = location.pathname.startsWith(formContextPage);

  const menuOptions: MenuOptionsType[] = [
    ...(isOnFormPage || disableCardClick
      ? []
      : [
          {
            name: "Open",
            link: routeFns.formContextPage(
              formContext.id,
              formContext.patientId,
            ),
            icon: "view",
            disabled: false,
          },
        ]),
    ...(!canDelete
      ? []
      : [
          {
            name: "Delete",
            onClick: () => {
              setShowDeleteModal(true);
            },
            icon: "delete",
            disabled: false,
          },
        ]),
    ...(canDownload
      ? [
          {
            icon: "view",
            onClick: async () => {
              setPdfViewFormId && setPdfViewFormId(mainForm.id);
            },
            name: "View PDF",
            disabled: false,
          },
          {
            name: "Download",
            onClick: async () => {
              const result = await api.forms.getPdf(
                mainForm.id,
                formContext.type === "admission",
              );
              if (result.status === 200) {
                const dataUri = `data:application/pdf;base64,${result.data}`;
                triggerDownload(dataUri, "form.pdf");
              }
            },
            icon: "download",
            disabled: false,
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
      }}
    >
      {showRadioSelector && (
        <Radio
          sx={{ mr: 1, color: "primary.main" }}
          checked={formContext.id === selectedRadioValue}
          onChange={(e) => {
            setSelectedRadioValue && setSelectedRadioValue(e.target.value);
          }}
          value={formContext.id}
          name="radio-button"
          inputProps={{ "aria-label": "radio-button-selector" }}
          checkedIcon={
            <CheckIcon
              css={css`
                color: "primary.main";
              `}
            />
          }
        />
      )}
      {showCheckBoxSelector && setCheckedFormItems && (
        <Checkbox
          sx={{ color: "primary.main", maxHeight: 100 }}
          checked={checkedFormItems[formContext.id]}
          onChange={() => {
            setCheckedFormItems((prevSelected: string[]) => {
              const isSelected = prevSelected.includes(formContext.id);
              return isSelected
                ? prevSelected.filter((id: string) => id !== formContext.id)
                : [...prevSelected, formContext.id];
            });
          }}
        />
      )}
      {showDeleteModal && (
        <ConfirmationModal
          message={t("components.confirmDeleteModal.message")}
          confirmFn={() => {
            const deleteClick = async () => {
              try {
                const result = await api.admissions.delete(formContext.id);
                if (result.status === 204) {
                  setShowDeleteModal(false);
                } else {
                  renderErrorToast({
                    message:
                      (result.data as any)?.reason ??
                      "Something went wrong while deleting the admission. Please try again later.",
                  });
                }
              } catch {
                setShowDeleteModal(false);
                renderErrorToast({
                  message:
                    "Something went wrong while deleting the admission. Please try again later.",
                });
              }
            };
            void deleteClick();
          }}
          closeFn={() => setShowDeleteModal(false)}
        />
      )}
      <FormRow
        menuOptions={disableCardClick ? [] : menuOptions}
        heading={heading}
        subHeading={subheading}
        summary={summary}
        onClick={
          !disableCardClick ? () => navigate(formContextPage) : undefined
        }
        lastMerged={lastMerged}
      />
    </Box>
  );
}
